import React from 'react';
import Slider from 'react-slick';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCategoryName } from '../lib/translations';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const NextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-green-50 transition-colors"
    style={{ transform: 'translate(50%, -50%)' }}
  >
    <ChevronRight className="w-5 h-5 text-slate-700" />
  </button>
);

const PrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-green-50 transition-colors"
    style={{ transform: 'translate(-50%, -50%)' }}
  >
    <ChevronLeft className="w-5 h-5 text-slate-700" />
  </button>
);

export default function CategoryCarousel({ categories, language, title, showViewAll = false, viewAllLink = '/products' }) {
  const settings = {
    dots: false,
    infinite: categories.length > 6,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 6,
    nextArrow: categories.length > 6 ? <NextArrow /> : null,
    prevArrow: categories.length > 6 ? <PrevArrow /> : null,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 5,
          slidesToScroll: 5,
          infinite: categories.length > 5,
          nextArrow: categories.length > 5 ? <NextArrow /> : null,
          prevArrow: categories.length > 5 ? <PrevArrow /> : null,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
          infinite: categories.length > 4,
          nextArrow: categories.length > 4 ? <NextArrow /> : null,
          prevArrow: categories.length > 4 ? <PrevArrow /> : null,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: categories.length > 3,
          nextArrow: categories.length > 3 ? <NextArrow /> : null,
          prevArrow: categories.length > 3 ? <PrevArrow /> : null,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: categories.length > 3,
          nextArrow: categories.length > 3 ? <NextArrow /> : null,
          prevArrow: categories.length > 3 ? <PrevArrow /> : null,
        }
      }
    ]
  };

  return (
    <div className="category-carousel-wrapper">
      {title && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
          {showViewAll && (
            <Link
              to={viewAllLink}
              className="text-green-600 hover:text-green-700 font-medium text-sm"
            >
              {language === 'hi' ? 'सभी देखें →' : 'View All →'}
            </Link>
          )}
        </div>
      )}

      <div className="relative px-2 sm:px-8">
        <Slider {...settings}>
          {categories.map((category) => (
            <div key={category.id} className="px-1 sm:px-2">
              <Link
                to={`/products?category=${category.id}`}
                className="block group"
              >
                <div className="bg-white rounded-xl p-2 sm:p-4 shadow-sm border border-slate-100 hover:shadow-md hover:border-green-200 transition-all h-full flex flex-col">
                  {/* Image */}
                  <div className="aspect-square rounded-lg overflow-hidden mb-2 sm:mb-3 bg-slate-100">
                    <img
                      src={category.image}
                      alt={getCategoryName(category, language)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  {/* Name — wraps naturally, no truncation */}
                  <h3 className="text-[11px] sm:text-sm font-medium text-slate-900 text-center group-hover:text-green-600 transition-colors leading-tight">
                    {getCategoryName(category, language)}
                  </h3>
                </div>
              </Link>
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
}
